import {
  ROLES_URL,
  ACCOUNT_USERS_URL,
  FILES_URL,
  TENANT_URL,
  BANNER_URL,
  USERS_URL,
} from '../../../utils/urls'
import { debounce } from 'lodash'
import { Role } from '../../role/Role'
import { FormItemProps, Rule } from 'antd/es/form'
import { NamePath } from 'antd/es/form/interface'
import { useEffect, useMemo, useState } from 'react'
import { Empty, Form, Radio, Select, Spin } from 'antd'
import { useAppSelector } from '../../../reducers/hooks'
import { Role as RoleModel } from '../../../models/Role'
import { TableParams } from '../../../models/TableParams'
import { SBModel } from '../../../utils/helpers/crud/models'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import { Tenant as TenantModel } from '../../../models/Tenant'
import { CentralUser } from '../../../models/CentralUser'
import { MetaData, MetaDataKey } from '../../../models/MetaData'
import { initialQuery } from '../../../utils/helpers/crud/models'
import { Actions } from '../../../modules/sbrm/components/Actions'
import { SBRMType, getMetadata } from '../../../modules/sbrm/SBRMModel'
import { SBAPIFetchPaginated } from '../../../utils/helpers/SBAPIHelper'

const { Option } = Select

type typesReturnType<T> = Record<
  SBRMType,
  {
    endpoint: string
    canbeRadio: boolean
    displayEmpty: boolean
    option: (item: T) => JSX.Element
    extra?: (item: T) => JSX.Element
    footer?: (shouldTriggerIndex: () => void) => JSX.Element
  }
>

export const SBRMTypeSelectInfos: typesReturnType<SBModel> = {
  [SBRMType.role]: {
    endpoint: ROLES_URL,
    canbeRadio: false,
    displayEmpty: true,
    option: (item: SBModel) => (
      <Role.Option model={item as unknown as RoleModel} />
    ),
  },
  [SBRMType.file]: {
    endpoint: FILES_URL,
    canbeRadio: false,
    displayEmpty: true,
    option: (item: SBModel) => <></>,
  },
  [SBRMType.user]: {
    endpoint: USERS_URL,
    canbeRadio: false,
    displayEmpty: true,
    option: (item: SBModel) => (
      <>
        #{(item as unknown as CentralUser).id} -{' '}
        {(item as unknown as CentralUser).firstName}{' '}
        {(item as unknown as CentralUser).lastName} -{' '}
        {(item as unknown as CentralUser).email}
      </>
    ),
  },
  [SBRMType.tenant]: {
    endpoint: TENANT_URL,
    canbeRadio: false,
    displayEmpty: true,
    option: (item: SBModel) => <>{(item as unknown as TenantModel).name}</>,
  },
  [SBRMType.banner]: {
    endpoint: BANNER_URL,
    canbeRadio: false,
    displayEmpty: true,
    option: (item: SBModel) => <></>,
  },
}

interface SBAsyncSelectProps {
  type: SBRMType
  name?: NamePath
  label?: string
  rules?: Rule[]
  multiple?: boolean
  metadata?: MetaData[]
  allowClear?: boolean | undefined
  style?: React.CSSProperties
  size?: SizeType
  placeholder?: string
  outsideSBRM?: boolean
}

export const SBAsyncSelect = <T extends SBModel>({
  type,
  name,
  label,
  rules,
  multiple = false,
  metadata,
  hidden,
  allowClear,
  style,
  size = 'large',
  placeholder = '',
  outsideSBRM = false,
}: SBAsyncSelectProps & FormItemProps) => {
  const form = Form.useFormInstance()
  const [items, setItems] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [query, setQuery] = useState<TableParams>(initialQuery)
  const [itemSelected, setItemSelected] = useState<T | undefined>(undefined)

  const { isOpen: SBRMIsOpen } = useAppSelector((state) => state.SBRM)
  const { isOpen: nestedIsOpen } = useAppSelector((state) => state.SBRM.nested)

  const handleSearch = (newValue: string) => {
    let newQuery: TableParams = {
      ...composeQueryFiltered(),
      search: { name: newValue },
    }

    if (!newValue) {
      delete newQuery.search
    }

    setQuery(newQuery)
    fetchDataForQuery(newQuery)
  }
  const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [metadata])

  const handleOnChange = (e: any) => {
    setItemSelected(items.find((i) => i.id === e))
  }

  const composeQueryFiltered = () => {
    // Instantiate a base query without any filter
    var query: TableParams = { ...initialQuery }

    // If needed, add filters
    if (!metadata) {
      return query
    }

    const keysToWrapInArray = [
      {
        metadataKey: MetaDataKey.eventId,
        filterKey: 'events',
        wrapInArray: true,
      },
      {
        metadataKey: MetaDataKey.venueId,
        filterKey: 'venues',
        wrapInArray: true,
      },
      {
        metadataKey: MetaDataKey.venuesId,
        filterKey: 'venues',
        wrapInArray: false,
      },
      {
        metadataKey: MetaDataKey.stationType,
        filterKey: 'type',
        wrapInArray: false,
      },
    ]
    for (const item of keysToWrapInArray) {
      const data = getMetadata(metadata, item.metadataKey)

      if (data === undefined) {
        continue
      }
      query = {
        ...query,
        filters: {
          [item.filterKey]: item.wrapInArray ? [data] : data,
        },
      }
    }

    return query
  }

  const fetchDataForQuery = (newQuery: TableParams) => {
    setIsLoading(true)
    SBAPIFetchPaginated<SBModel>(
      SBRMTypeSelectInfos[type].endpoint,
      newQuery
    ).then((data: any) => {
      setItems(data.data)
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (!SBRMIsOpen) {
      // We want to trigger only the reset
      // when the SBRM opens
      return
    }
    setItemSelected(undefined)

    const baseQuery = composeQueryFiltered()
    setQuery(baseQuery)
    fetchDataForQuery(baseQuery)
  }, [SBRMIsOpen, setQuery, metadata])

  useEffect(() => {
    if (!outsideSBRM) {
      return
    }
    // If the component is placed outside the SBRM
    // we need to trigger the initial request manually
    setItemSelected(undefined)

    const baseQuery = composeQueryFiltered()
    setQuery(baseQuery)
    fetchDataForQuery(baseQuery)
  }, [outsideSBRM])

  /**
   * When the field value changes make sure we always have the proper data to display
   * If the data is missing, only the ID will be displayed in the Select
   * Which is confusing for the user
   */
  useEffect(() => {
    const formValue = form.getFieldValue(name ?? '') // Get the form value
    if (!formValue) {
      // If there is no value in the field there is no missing data
      return
    }
    const value = !Array.isArray(formValue) ? [formValue] : formValue // If it's not an array, wrap it in array
    const existingIds = items.map((item) => item.id) // Map existing items to an array of IDs
    const missing = value.filter((id) => !existingIds.includes(id)) // Compute missing IDs
    // Fetch data for missing IDs
    const baseQuery = composeQueryFiltered()
    setQuery(baseQuery)
    fetchDataForQuery(baseQuery)
    fetchDataForQuery({ ...baseQuery, ids: missing })
  }, [form.getFieldValue(name ?? '')])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  })

  return (
    <>
      <Form.Item
        name={name}
        label={label}
        rules={rules}
        hidden={hidden}
        style={style}
      >
        {isLoading && SBRMTypeSelectInfos[type].canbeRadio ? (
          <Spin />
        ) : SBRMTypeSelectInfos[type].canbeRadio && items.length < 4 ? (
          <Radio.Group>
            {items.map((item) => (
              <Radio.Button key={item.id} value={item.id}>
                {SBRMTypeSelectInfos[type].option(item)}
              </Radio.Button>
            ))}
          </Radio.Group>
        ) : (
          <Select
            size={size}
            loading={isLoading}
            filterOption={false}
            showSearch={true}
            showArrow={true}
            onSearch={debouncedSearch}
            onChange={handleOnChange}
            onClick={() => debouncedSearch}
            allowClear={allowClear}
            mode={multiple ? 'multiple' : undefined}
            placeholder={placeholder}
            notFoundContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No ${type} found`}
              >
                {!nestedIsOpen && SBRMTypeSelectInfos[type].displayEmpty && (
                  <Actions actions={['create']} entity={type} />
                )}
              </Empty>
            }
            dropdownRender={
              SBRMTypeSelectInfos[type].footer !== undefined
                ? (menu) => (
                    <>
                      {menu}
                      {SBRMTypeSelectInfos[type].footer!(() =>
                        handleSearch('')
                      )}
                    </>
                  )
                : undefined
            }
          >
            {items.map((item) => (
              <Option key={item.id} value={item.id}>
                {SBRMTypeSelectInfos[type].option(item)}
              </Option>
            ))}
          </Select>
        )}
      </Form.Item>
      {itemSelected &&
        SBRMTypeSelectInfos[type].extra !== undefined &&
        SBRMTypeSelectInfos[type].extra!(itemSelected)}
    </>
  )
}
